<template>
  <div v-if="view">
    <div v-if="zone === 'buy' && hasBuy" class="mt-4 space-y-4">
      <p v-if="view.availability?.name || view.availability?.estimate" class="text-sm text-toned">
        <span v-if="view.availability?.name" class="font-medium text-highlighted">{{ view.availability.name }}</span>
        <span v-if="view.availability?.estimate"> · {{ view.availability.estimate }}</span>
      </p>

      <div v-if="view.badges.length" class="flex flex-wrap gap-2">
        <span
          v-for="badge in view.badges"
          :key="badge.label"
          class="rounded-full px-2.5 py-1 text-xs font-semibold"
          :class="badgeClass(badge.color)"
        >
          {{ badge.label }}
        </span>
      </div>

      <div v-if="view.family?.options.length" class="flex flex-wrap gap-2">
        <NuxtLink
          v-for="option in view.family.options"
          :key="option.slug"
          :to="shopPath(`/produse/${option.slug}`)"
          class="rounded-lg border px-3 py-1.5 text-sm"
          :class="option.current ? 'border-primary bg-primary/10 font-medium' : 'border-default hover:border-primary'"
        >
          {{ option.label }}
        </NuxtLink>
      </div>

      <div v-if="view.tiers?.tiers.length" class="rounded-xl border border-default p-3 text-sm">
        <p class="mb-2 font-medium text-highlighted">Preț în funcție de cantitate</p>
        <ul class="space-y-1">
          <li v-for="tier in view.tiers.tiers" :key="tier.minQty" class="flex justify-between gap-4">
            <span>de la {{ tier.minQty }} buc</span>
            <span class="font-medium">{{ formatPrice(tier.unitPrice) }}</span>
          </li>
        </ul>
        <p v-if="view.tiers.minQty" class="mt-2 text-xs text-muted">Cantitate minimă: {{ view.tiers.minQty }}</p>
      </div>

      <div v-if="view.refurbished?.isRefurbished" class="text-sm font-medium text-stone-700">Produs resigilat</div>
      <div v-if="view.refurbished?.offers.length" class="space-y-2">
        <p class="text-sm font-medium text-highlighted">Variante resigilate</p>
        <NuxtLink
          v-for="offer in view.refurbished.offers"
          :key="offer.slug"
          :to="shopPath(`/produse/${offer.slug}`)"
          class="flex items-center gap-3 rounded-lg border border-default p-2 text-sm hover:border-primary"
        >
          <img v-if="offer.image" :src="resolveMediaUrl(offer.image, apiOrigin)" alt="" class="h-12 w-12 object-contain">
          <span class="min-w-0 flex-1 truncate">{{ offer.name }}</span>
          <span class="font-medium">{{ formatPrice(offer.salePrice ?? offer.price) }}</span>
        </NuxtLink>
      </div>

      <div v-if="view.quickOrder">
        <UButton label="Comandă rapidă / cere ofertă" variant="outline" color="neutral" @click="formOpen = !formOpen" />
        <form v-if="formOpen" class="mt-3 space-y-3 rounded-xl border border-default p-4" @submit.prevent="submitRequest">
          <div class="flex gap-2">
            <UButton type="button" size="sm" :variant="requestType === 'order' ? 'solid' : 'outline'" label="Comandă" @click="requestType = 'order'" />
            <UButton type="button" size="sm" :variant="requestType === 'quote' ? 'solid' : 'outline'" label="Ofertă" @click="requestType = 'quote'" />
          </div>
          <UInput v-model="form.name" placeholder="Nume" required />
          <UInput v-model="form.phone" placeholder="Telefon" />
          <UInput v-model="form.email" type="email" placeholder="Email" />
          <UInputNumber v-model="form.quantity" :min="1" :max="9999" />
          <UTextarea v-model="form.message" :rows="3" placeholder="Mesaj" />
          <p v-if="formError" class="text-sm text-rose-600">{{ formError }}</p>
          <p v-if="formSent" class="text-sm text-emerald-700">Cererea a fost trimisă.</p>
          <UButton type="submit" label="Trimite" color="primary" :loading="formSending" />
        </form>
      </div>
    </div>

    <div v-else-if="hasAfter" class="mt-12 space-y-8">
      <a v-if="view.banner?.image" :href="view.banner.link || undefined" class="block overflow-hidden rounded-xl">
        <img :src="resolveMediaUrl(view.banner.image, apiOrigin)" alt="" class="w-full object-cover">
      </a>

      <section v-if="view.documents.length">
        <h2 class="mb-3 text-xl font-bold text-highlighted">Documente</h2>
        <ul class="space-y-2">
          <li v-for="doc in view.documents" :key="doc.id">
            <a :href="resolveMediaUrl(doc.fileUrl, apiOrigin)" target="_blank" rel="noopener" class="text-primary hover:underline">
              {{ doc.name }}
            </a>
          </li>
        </ul>
      </section>

      <section v-if="view.sizeChart?.image">
        <h2 class="mb-3 text-xl font-bold text-highlighted">Tabel de mărimi</h2>
        <img :src="resolveMediaUrl(view.sizeChart.image, apiOrigin)" alt="Tabel de mărimi" class="max-w-full rounded-xl">
      </section>

      <section v-if="view.questions">
        <h2 class="mb-4 text-xl font-bold text-highlighted">Întrebări</h2>
        <div v-if="view.questions.length" class="mb-6 space-y-4">
          <article v-for="item in view.questions" :key="item.id" class="rounded-xl border border-default p-4">
            <p class="font-medium text-highlighted">{{ item.authorName }}</p>
            <p class="mt-1 text-sm">{{ item.question }}</p>
            <p class="mt-2 text-sm text-toned">{{ item.answer }}</p>
          </article>
        </div>
        <form class="space-y-3" @submit.prevent="submitQuestion">
          <UInput v-model="questionForm.authorName" placeholder="Nume" required />
          <UInput v-model="questionForm.email" type="email" placeholder="Email (opțional)" />
          <UTextarea v-model="questionForm.question" :rows="3" placeholder="Întrebarea ta" required />
          <p v-if="questionError" class="text-sm text-rose-600">{{ questionError }}</p>
          <p v-if="questionSent" class="text-sm text-emerald-700">Întrebarea a fost trimisă și apare după răspuns.</p>
          <UButton type="submit" label="Trimite întrebarea" color="primary" :loading="questionSending" />
        </form>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
const catalogViewInflight = new Map<number, Promise<CatalogProductView | null>>()

export interface CatalogProductView {
  availability: { name: string | null, estimate: string | null, excludeLocker: boolean } | null
  badges: { label: string, color: string }[]
  banner: { image: string, link: string | null } | null
  sizeChart: { image: string } | null
  documents: { id: number, name: string, fileUrl: string }[]
  family: {
    externalUrl: string | null
    options: { slug: string, label: string, current: boolean }[]
  } | null
  refurbished: {
    isRefurbished: boolean
    offers: { slug: string, name: string, price: number, salePrice: number | null, image: string | null }[]
  } | null
  tiers: { minQty: number | null, tiers: { minQty: number, unitPrice: number }[] } | null
  questions: { id: number, authorName: string, question: string, answer: string | null }[] | null
  quickOrder: boolean
}
</script>

<script setup lang="ts">
import { resolveMediaUrl } from '~/utils/mediaUrl'

const props = defineProps<{
  productId: number
  zone: 'buy' | 'after'
}>()

const emit = defineEmits<{
  ready: [payload: { externalUrl: string | null, minQty: number | null }]
}>()

const { get, post } = useApi()
const shopPath = useShopPath()
const apiOrigin = useRuntimeConfig().public.apiOrigin as string
const views = useState<Record<number, CatalogProductView | null>>('catalog-module-product-views', () => ({}))

const view = computed(() => views.value[props.productId] ?? null)

const hasBuy = computed(() => {
  const current = view.value
  if (!current) return false
  return Boolean(
    current.availability?.name
    || current.availability?.estimate
    || current.badges.length
    || current.family?.options.length
    || current.tiers?.tiers.length
    || current.tiers?.minQty
    || current.refurbished?.isRefurbished
    || current.refurbished?.offers.length
    || current.quickOrder
  )
})

const hasAfter = computed(() => {
  const current = view.value
  if (!current) return false
  return Boolean(
    current.banner?.image
    || current.documents.length
    || current.sizeChart?.image
    || current.questions
  )
})

const BADGE_CLASS: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-800',
  sky: 'bg-sky-100 text-sky-800',
  amber: 'bg-amber-100 text-amber-900',
  rose: 'bg-rose-100 text-rose-800',
  violet: 'bg-violet-100 text-violet-800',
  slate: 'bg-slate-100 text-slate-700',
}

function badgeClass(color: string) {
  return BADGE_CLASS[color] || BADGE_CLASS.emerald
}

async function load(productId: number) {
  if (Object.prototype.hasOwnProperty.call(views.value, productId)) return views.value[productId]
  let pending = catalogViewInflight.get(productId)
  if (!pending) {
    pending = get<CatalogProductView>(`/catalog-modules/products/${productId}`)
      .then((response) => (response.success && response.data ? response.data : null))
      .catch(() => null)
    catalogViewInflight.set(productId, pending)
  }
  const data = await pending
  views.value = { ...views.value, [productId]: data }
  return data
}

watch(() => props.productId, async (productId) => {
  if (props.zone === 'buy') emit('ready', { externalUrl: null, minQty: null })
  const data = await load(productId)
  if (props.productId !== productId || props.zone !== 'buy') return
  emit('ready', {
    externalUrl: data?.family?.externalUrl || null,
    minQty: data?.tiers?.minQty ?? null,
  })
}, { immediate: true })

const formOpen = ref(false)
const requestType = ref<'order' | 'quote'>('order')
const form = reactive({ name: '', phone: '', email: '', message: '', quantity: 1 })
const formError = ref('')
const formSent = ref(false)
const formSending = ref(false)

async function submitRequest() {
  formError.value = ''
  formSent.value = false
  formSending.value = true
  try {
    await post('/catalog-modules/quick-requests', {
      type: requestType.value,
      productId: props.productId,
      name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
      quantity: form.quantity,
    })
    formSent.value = true
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Cererea nu a putut fi trimisă'
  } finally {
    formSending.value = false
  }
}

const questionForm = reactive({ authorName: '', email: '', question: '' })
const questionError = ref('')
const questionSent = ref(false)
const questionSending = ref(false)

async function submitQuestion() {
  questionError.value = ''
  questionSent.value = false
  questionSending.value = true
  try {
    await post('/catalog-modules/questions', {
      productId: props.productId,
      authorName: questionForm.authorName,
      email: questionForm.email,
      question: questionForm.question,
    })
    questionSent.value = true
    questionForm.question = ''
  } catch (err) {
    questionError.value = err instanceof Error ? err.message : 'Întrebarea nu a putut fi trimisă'
  } finally {
    questionSending.value = false
  }
}
</script>
